import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'
import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {replaceActorsForAssignableRelayMutation as replaceActorsForAssignableMutationQueryId} from '@github-ui/server-persisted-queries'
import type {ReplaceActorsForAssignableRelayMutationMutation} from '@github-ui/server-persisted-queries/types/replaceActorsForAssignableRelayMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {commitLocalUpdate, type Environment, type RecordSourceProxy} from 'relay-runtime'

export type AssigneePickerAssignee = AssigneePickerBaseAssignee & {
  type?: string
}

type AssignedActor = {
  __typename?: string
  id?: string
  login?: string
  name?: string | null
  profileResourcePath?: string | null
  avatarUrl?: string
  isCopilot?: boolean | null
  isAgent?: boolean | null
  displayName?: string | null
}

type Assignable = NonNullable<
  NonNullable<ReplaceActorsForAssignableRelayMutationMutation['replaceActorsForAssignable']>['assignable']
>
type IssueAssignableForStore = Extract<Assignable, {__typename: 'Issue'}>
type RelayAssignable = Assignable | null
type GeneratedAgentAssignment = NonNullable<IssueAssignableForStore['agentAssignments']>[number]
type AgentAssignment = ReadonlyArray<
  | {
      assignee?: GeneratedAgentAssignment['assignee'] | undefined
      assigner?: {id?: string; login?: string; __typename?: string} | null | undefined
    }
  | null
  | undefined
> | null

const ASSIGNED_ACTORS_CONNECTION_ARGS = {first: 20} as const
const PARTICIPANTS_CONNECTION_ARGS = {first: 10} as const
const fallbackErrorMessage = 'Failed to update assignees'
let assigneeMutationOperationSequence = 0

/** Response nodes come from an `... on Actor` inline fragment, so every field is optional. */
function hasActorIdentity(
  actor: AssignedActor | null | undefined,
): actor is AssignedActor & {id: string; login: string; avatarUrl: string} {
  return actor?.id != null && actor.login != null && actor.avatarUrl != null
}

function toRelayAssignable(assignable: RelayAssignable): IssueAssignableForStore | null {
  return assignable && '__typename' in assignable && assignable.__typename === 'Issue' ? assignable : null
}

function writeRelayAssignable(
  store: RecordSourceProxy,
  issueId: string,
  assignable: RelayAssignable,
  recordKey: string,
) {
  const issueAssignable = toRelayAssignable(assignable)
  const issueRecord = store.get(issueId)
  if (!issueAssignable || !issueRecord) return

  const assignedActorsConnection = issueRecord.getOrCreateLinkedRecord(
    'assignedActors',
    'ActorConnection',
    ASSIGNED_ACTORS_CONNECTION_ARGS,
  )
  const assignedActorNodes =
    issueAssignable.assignedActors?.nodes?.map(actor => {
      if (!actor?.id) return null
      const actorRecord = store.get(actor.id) ?? store.create(actor.id, actor.__typename)
      actorRecord.setValue(actor.id, 'id')
      actorRecord.setValue(actor.login ?? null, 'login')
      actorRecord.setValue(actor.name ?? null, 'name')
      actorRecord.setValue(actor.profileResourcePath ?? null, 'profileResourcePath')
      actorRecord.setValue(actor.avatarUrl ?? null, 'avatarUrl')
      if ('isCopilot' in actor) actorRecord.setValue(actor.isCopilot ?? null, 'isCopilot')
      if ('isAgent' in actor) actorRecord.setValue(actor.isAgent ?? null, 'isAgent')
      if ('displayName' in actor) actorRecord.setValue(actor.displayName ?? null, 'displayName')
      return actorRecord
    }) ?? []
  assignedActorsConnection.setLinkedRecords(assignedActorNodes, 'nodes')

  const participantsConnection = issueRecord.getOrCreateLinkedRecord(
    'participants',
    'UserConnection',
    PARTICIPANTS_CONNECTION_ARGS,
  )
  const participantNodes =
    issueAssignable.participants?.nodes?.map(participant => {
      if (!participant?.id) return null
      const participantRecord = store.get(participant.id) ?? store.create(participant.id, 'User')
      participantRecord.setValue(participant.id, 'id')
      participantRecord.setValue(participant.login ?? null, 'login')
      participantRecord.setValue(participant.name ?? null, 'name')
      participantRecord.setValue(participant.profileResourcePath ?? null, 'profileResourcePath')
      participantRecord.setValue(participant.avatarUrl ?? null, 'avatarUrl')
      participantRecord.setValue('User', '__typename')
      return participantRecord
    }) ?? []
  participantsConnection.setLinkedRecords(participantNodes, 'nodes')

  const assignmentRecords = (issueAssignable.agentAssignments ?? []).map((assignment, index) => {
    const assignmentRecordId = `client:${recordKey}:agentAssignment:${index}`
    const assignmentRecord = store.get(assignmentRecordId) ?? store.create(assignmentRecordId, 'AgentAssignment')

    if (assignment?.assignee?.id) {
      const existingAssigneeRecord = store.get(assignment.assignee.id)
      const assigneeType =
        existingAssigneeRecord?.getType() ??
        (assignment.assignee.isCopilot ||
        assignment.assignee.isAgent ||
        assignment.assignee.displayName != null ||
        assignment.assignee.login?.includes('[bot]')
          ? 'Bot'
          : 'User')
      const assigneeRecord = existingAssigneeRecord ?? store.create(assignment.assignee.id, assigneeType)
      assigneeRecord.setValue(assignment.assignee.id, 'id')
      assigneeRecord.setValue(assignment.assignee.login ?? null, 'login')
      assigneeRecord.setValue(assignment.assignee.avatarUrl ?? null, 'avatarUrl')
      assigneeRecord.setValue(assignment.assignee.isCopilot ?? null, 'isCopilot')
      assigneeRecord.setValue(assignment.assignee.isAgent ?? null, 'isAgent')
      assigneeRecord.setValue(assignment.assignee.displayName ?? null, 'displayName')
      assigneeRecord.setValue(assigneeType, '__typename')
      assignmentRecord.setLinkedRecord(assigneeRecord, 'assignee')
    } else {
      assignmentRecord.setValue(null, 'assignee')
    }

    if (assignment?.assigner?.id) {
      const assignerType = assignment.assigner.__typename ?? 'User'
      const assignerRecord = store.get(assignment.assigner.id) ?? store.create(assignment.assigner.id, assignerType)
      assignerRecord.setValue(assignment.assigner.id, 'id')
      assignerRecord.setValue(assignment.assigner.login ?? null, 'login')
      assignerRecord.setValue(assignerType, '__typename')
      assignmentRecord.setLinkedRecord(assignerRecord, 'assigner')
    } else {
      assignmentRecord.setValue(null, 'assigner')
    }

    return assignmentRecord
  })
  issueRecord.setLinkedRecords(assignmentRecords, 'agentAssignments')
}

async function replaceActorsForAssignableRequest({
  owner,
  repository,
  issueNumber,
  assignedActors,
  clientMutationId,
  signal,
}: {
  owner: string
  repository: string
  issueNumber: number
  assignedActors: AssigneePickerAssignee[]
  clientMutationId?: string
  signal: AbortSignal
}): Promise<{
  data: ReplaceActorsForAssignableRelayMutationMutation
  errors?: ReadonlyArray<{message?: string}> | null
}> {
  const query = new URLSearchParams({queryId: replaceActorsForAssignableMutationQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repository)}/issues/${issueNumber}/assigned_actors.json?${query.toString()}`,
    {
      method: 'PATCH',
      body: {
        actorIds: assignedActors.map(a => a.id),
        ...(clientMutationId ? {clientMutationId} : {}),
      },
      signal,
    },
  )

  if (!response.ok) {
    throw new Error(`${fallbackErrorMessage} (${response.status})`)
  }

  const result = (await response.json()) as {
    data?: ReplaceActorsForAssignableRelayMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  if (!result.data) {
    throw new Error(fallbackErrorMessage)
  }

  return {data: result.data, errors: result.errors}
}

/** Project mutation actors into the row-consumer assignee shape used by paved-path optimistic data. */
export function toAssigneesOperation(
  issueId: string,
  assignedActors: ReadonlyArray<AssignedActor | null | undefined>,
): SetFieldsOperation {
  const actors = assignedActors.filter(hasActorIdentity)
  const toAssignedActorNode = (actor: (typeof actors)[number]) => {
    const typename = actor.__typename === 'Bot' ? 'Bot' : 'User'
    return {
      __typename: typename,
      __isActor: typename,
      __isNode: typename,
      id: actor.id,
      login: actor.login,
      avatarUrl: actor.avatarUrl,
      ...(actor.name != null ? {name: actor.name} : {}),
      ...(actor.profileResourcePath != null ? {profileResourcePath: actor.profileResourcePath} : {}),
      ...(typename === 'Bot'
        ? {
            isCopilot: actor.isCopilot ?? false,
            isAgent: actor.isAgent ?? false,
            ...(actor.displayName != null ? {displayName: actor.displayName} : {}),
          }
        : {}),
    }
  }
  return {
    type: 'setFields',
    id: issueId,
    fields: {
      assignedActors: {nodes: actors.map(toAssignedActorNode)},
    },
  }
}

type UpdateIssueAssigneesMutationInput = {
  issueId: string
  owner: string
  repository: string
  issueNumber: number
  assignedActors: AssigneePickerAssignee[]
  participants: AssigneePickerAssignee[]
}

type UpdateIssueAssigneesMutationOptions = {
  input: UpdateIssueAssigneesMutationInput
  existingAgentAssignments?: AgentAssignment
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: ReplaceActorsForAssignableRelayMutationMutation) => void
}

type UpdateIssueAssigneesMutationVariables = UpdateIssueAssigneesMutationOptions & {
  operation: SetFieldsOperation
  operationId: string
  abortController: AbortController
}

export function useUpdateIssueAssigneesMutation() {
  const {mutateAsync} = useMutation<
    {data: ReplaceActorsForAssignableRelayMutationMutation; errors?: ReadonlyArray<{message?: string}> | null},
    Error,
    UpdateIssueAssigneesMutationVariables
  >({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables =>
      replaceActorsForAssignableRequest({
        owner: variables.input.owner,
        repository: variables.input.repository,
        issueNumber: variables.input.issueNumber,
        assignedActors: variables.input.assignedActors,
        clientMutationId: variables.clientMutationId,
        signal: variables.abortController.signal,
      }),
    onSuccess: async (mutationResult, variables) => {
      try {
        const response = mutationResult.data
        const errors = mutationResult.errors
        const confirmedActors = toRelayAssignable(response.replaceActorsForAssignable?.assignable ?? null)
          ?.assignedActors?.nodes
        if (!confirmedActors) {
          throw new Error(errors?.[0]?.message ?? fallbackErrorMessage)
        }
        if (confirmedActors.every(hasActorIdentity)) {
          await notifySettlement(
            toAssigneesOperation(variables.input.issueId, confirmedActors),
            ENTITY_FIELDS_OWNER,
            DOCUMENT_FIELDS_PATH,
            variables.operationId,
          )
        }
        variables.onCompleted?.(response)
      } catch (error) {
        variables.onError?.(error instanceof Error ? error : new Error(fallbackErrorMessage))
      }
    },
    onError: (error, variables) => {
      const normalizedError = variables.abortController.signal.aborted
        ? new Error('Updating assignees was cancelled')
        : error instanceof Error
          ? error
          : new Error(fallbackErrorMessage)
      variables.onError?.(normalizedError)
    },
  })

  return useCallback(
    (options: UpdateIssueAssigneesMutationOptions) => {
      const abortController = new AbortController()
      const operation = toAssigneesOperation(options.input.issueId, options.input.assignedActors)
      const operationId = `replace-actors-for-assignable-${assigneeMutationOperationSequence++}`
      void (async () => {
        try {
          await mutateAsync({...options, operation, operationId, abortController})
        } catch {
          // handled in hook callbacks
        }
      })()
      return {dispose: () => abortController.abort()}
    },
    [mutateAsync],
  )
}

export function buildOptimisticResponse(
  issueId: string,
  assignedActors: AssigneePickerAssignee[],
  newParticipants: AssigneePickerAssignee[],
  existingAgentAssignments?: AgentAssignment,
) {
  return {
    replaceActorsForAssignable: {
      assignable: {
        __typename: 'Issue',
        __isNode: 'Issue',
        id: issueId,
        assignedActors: {
          nodes: assignedActors.map(a => {
            const isBotAssignee = a.__typename === 'Bot'
            if (isBotAssignee) {
              return {
                __typename: 'Bot',
                __isActor: 'Bot',
                __isNode: 'Bot',
                id: a.id,
                login: a.login,
                name: a.name ?? null,
                avatarUrl: a.avatarUrl,
                profileResourcePath: a.profileResourcePath ?? null,
                displayName: a.displayName ?? '',
                isCopilot: a.isCopilot || false,
                isAgent: a.isAgent || false,
              }
            } else {
              return {
                __typename: 'User',
                __isActor: 'User',
                __isNode: 'User',
                id: a.id,
                login: a.login,
                name: a.name ?? null,
                avatarUrl: a.avatarUrl,
                profileResourcePath: a.profileResourcePath ?? null,
              }
            }
          }),
        },
        agentAssignments: (existingAgentAssignments ?? []).map(a => ({
          assignee: a?.assignee
            ? {
                id: a.assignee.id,
                login: a.assignee.login,
                avatarUrl: a.assignee.avatarUrl,
                isCopilot: a.assignee.isCopilot,
                isAgent: a.assignee.isAgent,
                displayName: a.assignee.displayName,
              }
            : null,
          assigner:
            a?.assigner && a.assigner.id
              ? {
                  __typename: a.assigner.__typename,
                  __isActor: a.assigner.__typename,
                  __isNode: a.assigner.__typename,
                  id: a.assigner.id,
                  login: a.assigner.login,
                }
              : null,
        })),
        participants: {
          nodes: newParticipants.map(a => ({
            id: a.id,
            login: a.login,
            name: a.name ?? null,
            avatarUrl: a.avatarUrl,
            profileResourcePath: a.profileResourcePath ?? null,
          })),
        },
      },
    },
  }
}

export function commitUpdateIssueAssigneesMutation({
  environment,
  input: {issueId, owner, repository, issueNumber, assignedActors, participants},
  existingAgentAssignments,
  clientMutationId,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {
    issueId: string
    owner: string
    repository: string
    issueNumber: number
    assignedActors: AssigneePickerAssignee[]
    participants: AssigneePickerAssignee[]
  }
  existingAgentAssignments?: AgentAssignment
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: ReplaceActorsForAssignableRelayMutationMutation) => void
}) {
  // merge assigned actors and participants into one, unique by login and take the first 5
  // do not include copilot or agents because bots do not show up as participants on issues
  // (the participants field on Issue returns UserConnection, which only contains User types)
  const newParticipants = [
    ...new Map(
      [...assignedActors, ...participants].filter(a => !a.isCopilot && !a.isAgent).map(item => [item.login, item]),
    ).values(),
  ].slice(0, 5)
  const relayWriteKey = `${issueId}:tanstack`

  const optimisticRelayResponse = buildOptimisticResponse(
    issueId,
    assignedActors,
    newParticipants,
    existingAgentAssignments,
  )
  const relayOptimisticUpdate = environment.applyUpdate({
    storeUpdater: store =>
      writeRelayAssignable(
        store,
        issueId,
        (optimisticRelayResponse.replaceActorsForAssignable?.assignable as Assignable | null) ?? null,
        `${relayWriteKey}:optimistic`,
      ),
  })
  let relayOptimisticDisposed = false
  const disposeRelayOptimistic = () => {
    if (relayOptimisticDisposed) return
    relayOptimisticDisposed = true
    relayOptimisticUpdate?.dispose()
  }

  const fail = (_error: Error) => {
    disposeRelayOptimistic()
  }

  let settled = false
  let relayDisposable: {dispose: () => void} | undefined

  const settleError = (error: Error) => {
    if (settled) return
    settled = true
    fail(error)
    onError?.(error)
  }

  const settleSuccess = (response: ReplaceActorsForAssignableRelayMutationMutation) => {
    if (settled) return
    settled = true
    onCompleted?.(response)
  }

  const executeTanstackMutation = async () => {
    const abortController = new AbortController()
    relayDisposable = {
      dispose() {
        if (settled) return
        settled = true
        abortController.abort()
        fail(new Error('Updating assignees was cancelled'))
      },
    }

    try {
      const mutationResult = await replaceActorsForAssignableRequest({
        owner,
        repository,
        issueNumber,
        assignedActors,
        clientMutationId,
        signal: abortController.signal,
      })
      const response = mutationResult.data
      const errors = mutationResult.errors

      commitLocalUpdate(environment, store =>
        writeRelayAssignable(
          store,
          issueId,
          response.replaceActorsForAssignable?.assignable ?? null,
          `${relayWriteKey}:confirmed`,
        ),
      )
      disposeRelayOptimistic()

      const confirmedActors = toRelayAssignable(response.replaceActorsForAssignable?.assignable ?? null)?.assignedActors
        ?.nodes
      if (!confirmedActors) {
        settleError(new Error(errors?.[0]?.message ?? fallbackErrorMessage))
        return
      }
      if (!confirmedActors.every(hasActorIdentity)) {
        // The write completed, but an incomplete collection cannot safely settle mirrored consumers.
        settleSuccess(response)
        return
      }

      settleSuccess(response)
    } catch (error) {
      if (settled) return
      settleError(error instanceof Error ? error : new Error(fallbackErrorMessage))
    }
  }
  void executeTanstackMutation()

  return {
    dispose() {
      relayDisposable?.dispose()
    },
  }
}
